import URL from 'url';
import Cookies from 'universal-cookie';
import { COOKIE_MAX_AGE } from './cookie';
import { getPartnerIdFromPath } from 'utils/container';

const {
  NODE_ENV,
  CLIENT_URL,
  BASE_URI,
  ADMIN_SUB,
  PARTNER_SUB,
  PARTNER_HOST,
  PARTNER_URL,
} = process.env;

const cookies = new Cookies();

let loggedIn = undefined;
let sessionId = undefined;
let sessionClient = undefined;
let sessionPartnerId = undefined;

const Auth = {
  isLoggedIn(account) {
    return loggedIn;
  },

  buildSessionHeaders() {
    const partnerId = getPartnerIdFromPath();

    return {
      'X-Session': `${this.getSession()}`,
      ...(partnerId && { 'X-Partner': partnerId }),
    };
  },

  setSession(session) {
    if (!session?.id || (!session.client_id && !session.partner_id)) {
      this.removeSessionCookie();
      loggedIn = false;
      sessionId = null;
      sessionClient = null;
      sessionPartnerId = null;

      return;
    }

    loggedIn = Boolean(session.user_id);
    sessionId = session.id;

    if (session.client_id) {
      sessionClient = session.client_id;
    } else if (session.partner_id) {
      sessionClient = PARTNER_SUB;
      sessionPartnerId = session.partner_id;
    }

    this.setSessionCookie();
  },

  setLoggedInSessionId(id, clientId) {
    loggedIn = true;
    sessionId = id;
    sessionClient = clientId;
    this.setSessionCookie();
  },

  setLoggedInPartnerSessionId(id, partnerId) {
    // Set before the cookie is written so it lands in the partner's slot
    sessionPartnerId = partnerId;
    this.setLoggedInSessionId(id, PARTNER_SUB);
  },

  // Partner sessions are scoped to the partner in the first path segment,
  // so one browser can hold a session per partner on the same host
  getSessionCookieName(partnerId = sessionPartnerId || getPartnerIdFromPath()) {
    return partnerId ? `_session_id_${partnerId}` : '_session_id';
  },

  getSessionPartnerId() {
    return sessionPartnerId;
  },

  // One-release migration from the pre-scoping bare session cookie: offer it
  // as the boot session when the partner's own cookie is empty. A valid
  // session is then adopted into its partner's cookie by setSession()
  getLegacySessionCandidate(partnerId) {
    if (
      !partnerId ||
      sessionId ||
      cookies.get(this.getSessionCookieName(partnerId))
    ) {
      return null;
    }

    const legacyId = cookies.get('_session_id') || null;

    if (legacyId) {
      // getSession() returns this for the boot fetch
      sessionId = legacyId;
    }

    return legacyId;
  },

  removeLegacySessionCookie() {
    cookies.remove('_session_id', this.getCookieOptions());
  },

  getSession() {
    if (sessionId) {
      return sessionId;
    }
    return (sessionId = cookies.get(this.getSessionCookieName()));
  },

  setSessionCookie() {
    if (!sessionId) {
      return;
    }
    cookies.set(
      this.getSessionCookieName(),
      sessionId,
      this.getCookieOptions(),
    );
  },

  removeSessionCookie(partnerId) {
    cookies.remove(
      this.getSessionCookieName(partnerId),
      this.getCookieOptions(),
    );
  },

  getSessionClient() {
    switch (sessionClient) {
      case 'admin':
        return ADMIN_SUB;
      case PARTNER_SUB:
      default:
        return sessionClient;
    }
  },

  getSessionClientUrl() {
    if (!CLIENT_URL) {
      throw new Error('Missing CLIENT_URL');
    }

    return CLIENT_URL.replace('CLIENT_ID', this.getSessionClient());
  },

  getCookieDomain() {
    if (sessionClient) {
      return URL.parse(this.getSessionClientUrl()).host.replace(/:.*/, '');
    }
    if (window.location.hostname === PARTNER_HOST) {
      return PARTNER_HOST;
    }
    if (!window.location) {
      return null;
    }
    if (window.location.hostname === 'localhost') {
      return null;
    }
    return `.${window.location.hostname.split('.').slice(-2).join('.')}`;
  },

  getCookieOptions() {
    return {
      path: '/',
      maxAge: COOKIE_MAX_AGE,
      domain: this.getCookieDomain(),
      // client cookies cannot be http only
      httpOnly: false,
      sameSite: 'lax',
      secure: NODE_ENV !== 'development',
    };
  },

  redirectClientSessionTo(
    url = '',
    router = null,
    options = { forceRedirect: false },
  ) {
    const { forceRedirect } = options;
    const clientUrl = this.getSessionClientUrl();

    if (router && (forceRedirect || clientUrl === window.location.origin)) {
      router.replace(decodeURIComponent(url || ''));
    } else {
      window.location.href = `${clientUrl}${BASE_URI}/login/${sessionId}?to=${
        url || ''
      }`;
    }
  },

  redirectPartnerSessionTo(url = '', router = null) {
    if (router) {
      router.replace(decodeURIComponent(url || ''));
    } else {
      window.location.href = `${PARTNER_URL}/${PARTNER_SUB}/${sessionPartnerId}/login/${sessionId}?to=${
        url || ''
      }`;
    }
  },

  // The partner signup lives on its own host. Only the host differs, so the
  // port comes from where we are — `PARTNER_URL` addresses the api, not the app.
  getPartnerSignupUrl() {
    return this.getPartnerAppUrl('/signup');
  },

  getPartnerAppUrl(path = '') {
    const { protocol, port } = window.location;

    return `${protocol}//${PARTNER_HOST}${port ? `:${port}` : ''}${path}`;
  },

  getSignupUrl() {
    const { protocol, hostname, port } = window.location;
    let basename = hostname.split('.');
    if (basename.length > 2) {
      basename =
        // support dev server: dev.swell.store, test.dev.swell.store, mystore.dev.swell.store -> dev.swell.store/signup
        basename[basename.length - 3] === 'dev'
          ? basename.slice(basename.length - 3)
          : basename.slice(1);
    }
    return `${protocol}//${basename.join('.')}${port ? `:${port}` : ''}/signup`;
  },
};

export default Auth;
